import { useEffect, useRef } from 'react'

import { useLazyQuery } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import { Divider, Text, Tooltip } from '@mantine/core'
import { Copy, ExternalLink } from 'lucide-react'

import { CollapsibleSection } from 'src/components/CollapsibleSection'
import DnDrawer from 'src/components/Core/DnDrawer'
import DnStepper from 'src/components/Core/DnStepper'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import AttachmentsRow from 'src/components/Notifications/Components/AttachmentsRow'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_APPLICANT_BY_ID } from 'src/graphql/applicants.graphql'
import {
  GET_PACKAGE_ORDERS,
  GET_PACKAGE_ORDER_ENHANCED_REPORT,
  GET_PACKAGE_ORDER_DRIVER_IQ_LEAD_TYPES,
} from 'src/graphql/packageOrders.graphql'
import { GET_SIDEBAR_CHECKLIST_STEPS } from 'src/graphql/steps.graphql'
import { DocumentCategoryLabel } from 'src/graphql/types/documents'
import { PackageTypeLabels } from 'src/graphql/types/package'
import { formatDateCustom, formatSnakeValue } from 'src/lib/formatters'

import AdminInngestSection from './components/AdminInngestSection'
import {
  getItemStatusDotColor,
  getItemStatusVariant,
  getOrderStatusVariant,
} from './helpers'

// The provider's own verdict for a product row (DriverIQ "Clear" / "Adverse",
// HireRight "Pass" / "Review" / "Fail", ...). Rendered verbatim and neutral:
// we don't map or color provider wording because it can change under us.
const ProviderResultTag = ({
  result,
  details,
}: {
  result?: string | null
  details?: string | null
}) => {
  if (!result) return null
  const tag = (
    <Tag
      variant="outline"
      showCloseButton={false}
      size="xs"
      data-testid="provider-result"
    >
      {result}
    </Tag>
  )
  if (!details) return tag
  return (
    <Tooltip label={details} multiline w={280}>
      <span>{tag}</span>
    </Tooltip>
  )
}

const OrderDetails = ({ packageOrder, opened, onClose, applicantId }) => {
  const approveRefetchQueries = applicantId
    ? [
        { query: GET_SIDEBAR_CHECKLIST_STEPS, variables: { applicantId } },
        { query: GET_APPLICANT_BY_ID, variables: { id: applicantId } },
        getOperationName(GET_PACKAGE_ORDERS),
      ]
    : []

  const [getDriverIQLeadTypes, { data: driverIQLeadTypesData }] = useLazyQuery(
    GET_PACKAGE_ORDER_DRIVER_IQ_LEAD_TYPES
  )
  const driverIQLeadTypesOrderIdRef = useRef<string | null>(null)

  // Fetch DriverIQ lead types when the drawer opens for a DriverIQ order
  useEffect(() => {
    if (
      opened &&
      packageOrder?.packageOrderId &&
      packageOrder?.orderType === 'DRIVER_IQ'
    ) {
      driverIQLeadTypesOrderIdRef.current = packageOrder.packageOrderId
      getDriverIQLeadTypes({
        variables: { packageOrderId: packageOrder.packageOrderId },
      })
    } else {
      driverIQLeadTypesOrderIdRef.current = null
    }
  }, [
    opened,
    packageOrder?.packageOrderId,
    packageOrder?.orderType,
    getDriverIQLeadTypes,
  ])

  const [getEnhancedReport] = useLazyQuery(GET_PACKAGE_ORDER_ENHANCED_REPORT, {
    onCompleted: (data) => {
      if (data?.getPackageOrderEnhancedReport?.url) {
        window.open(data.getPackageOrderEnhancedReport.url, '_blank')
      }
    },
    onError: () => {
      toast('Failed to generate enhanced report', 'error')
    },
  })

  const handleEnhancedReportClick = (packageOrderId) => {
    getEnhancedReport({
      variables: {
        packageOrderId,
      },
    })
  }

  // Group documents by category and sort
  const documentsByCategory =
    packageOrder?.documents?.reduce((acc, doc) => {
      const category = doc.category || 'Uncategorized'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(doc)
      return acc
    }, {}) || {}

  const sortedCategories = Object.keys(documentsByCategory).sort()

  // Sort activity logs by createdAt descending (most recent first)
  const activityLogs = packageOrder?.activityLogs || []
  const sortedActivityLogs = [...activityLogs].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0
    return bTime - aTime
  })

  const items = packageOrder?.items || []
  // Only use DriverIQ lead types if they belong to the currently selected order
  const driverIQLeadTypes =
    driverIQLeadTypesOrderIdRef.current === packageOrder?.packageOrderId
      ? driverIQLeadTypesData?.getPackageOrderDriverIQLeadTypes || []
      : []
  const isDriverIQ = packageOrder?.orderType === 'DRIVER_IQ'

  return (
    <DnDrawer opened={opened} onClose={onClose}>
      <div className="flex h-screen flex-col space-y-4 ">
        {/* HEADER */}
        <div className="flex flex-row items-center gap-2">
          <Text size="lg" fw={500}>
            {packageOrder?.package?.name || ''}
          </Text>
          <Tooltip label="Copy Package Order ID">
            <Copy
              size={12}
              strokeWidth={1}
              className="cursor-pointer hover:text-doubleNickel-gray-500"
              onClick={() => {
                navigator.clipboard.writeText(packageOrder?.packageOrderId)
                toast('Copied Package Order ID to clipboard', 'success')
              }}
            />
          </Tooltip>
        </div>
        <Divider variant="dashed" />

        {/* BODY */}
        <div className="flex h-screen flex-col space-y-4 overflow-auto">
          <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
            ORDER DETAILS
          </Text>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Provider Order ID
            </Text>
            <div className="flex flex-row items-center gap-2">
              <Text size="xs" fw={300}>
                {packageOrder?.providerOrderId || ''}
              </Text>
              <Copy
                size={10}
                strokeWidth={1}
                className="cursor-pointer hover:text-doubleNickel-gray-500"
                onClick={() => {
                  navigator.clipboard.writeText(packageOrder?.providerOrderId)
                  toast('Copied Provider Order ID to clipboard', 'success')
                }}
              />
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Provider
            </Text>
            <div className="flex flex-row items-center gap-2">
              <Text size="xs" fw={300}>
                {PackageTypeLabels[packageOrder?.orderType]}
              </Text>
              {packageOrder?.orderType === 'HIRE_RIGHT' &&
                packageOrder?.providerOrderId && (
                  <ExternalLink
                    size={10}
                    strokeWidth={1}
                    className="cursor-pointer hover:text-doubleNickel-gray-500"
                    onClick={() => {
                      handleEnhancedReportClick(packageOrder?.packageOrderId)
                    }}
                  />
                )}
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Status
            </Text>
            <Tag
              variant={getOrderStatusVariant(packageOrder?.status)}
              showCloseButton={false}
              size="xs"
            >
              {formatSnakeValue(packageOrder?.status)}
            </Tag>
          </div>

          {packageOrder?.currentLicenseOnly && (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                License scope
              </Text>
              <Text size="xs" fw={300} data-testid="license-scope">
                Current license only
              </Text>
            </div>
          )}

          {packageOrder?.completedDate && (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                Completed on
              </Text>
              <Text size="xs" fw={300}>
                {formatDateCustom(
                  packageOrder?.completedDate,
                  'MM/DD/YYYY hh:mm A'
                )}
              </Text>
            </div>
          )}

          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Created at
            </Text>
            <Text size="xs" fw={300}>
              {formatDateCustom(packageOrder?.createdAt, 'MM/DD/YYYY hh:mm A')}
            </Text>
          </div>

          {/* INNGEST (ADMIN) — internal admins only, DriverIQ orders only */}
          <AdminInngestSection packageOrder={packageOrder} />

          {/* PRODUCTS — DriverIQ orders show lead types from case/detail, others show DB items */}
          <Divider variant="dashed" />
          <CollapsibleSection title="PRODUCTS" defaultOpen={true}>
            {isDriverIQ && driverIQLeadTypes.length > 0 ? (
              <div>
                {driverIQLeadTypes.map((lead, index) => (
                  <div
                    key={`${lead.verificationSource}-${index}`}
                    className="flex items-center justify-between border-b border-gray-100 py-2 last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 flex-shrink-0 rounded-full ${getItemStatusDotColor(
                          lead.status
                        )}`}
                      />
                      <Text size="xs" fw={300}>
                        {lead.verificationSource}
                      </Text>
                    </div>
                    <div className="flex items-center gap-2">
                      <ProviderResultTag result={lead.result} />
                      <Tag
                        variant={getItemStatusVariant(lead.status)}
                        showCloseButton={false}
                        size="xs"
                      >
                        {formatSnakeValue(lead.status)}
                      </Tag>
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length > 0 ? (
              <div>
                {items.map((item) => (
                  <div
                    key={item.packageOrderItemId}
                    className="flex items-center justify-between border-b border-gray-100 py-2 last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 flex-shrink-0 rounded-full ${getItemStatusDotColor(
                          item.status
                        )}`}
                      />
                      <Text size="xs" fw={300}>
                        {item.productName || item.productType || 'Unknown'}
                      </Text>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.completedAt && (
                        <Text
                          size="xs"
                          fw={300}
                          className="text-doubleNickel-gray-400"
                        >
                          {formatDateCustom(item.completedAt, 'MM/DD/YYYY')}
                        </Text>
                      )}
                      <ProviderResultTag
                        result={item.providerResult}
                        details={item.providerResultDetails}
                      />
                      <Tag
                        variant={getItemStatusVariant(item.status)}
                        showCloseButton={false}
                        size="xs"
                      >
                        {formatSnakeValue(item.status)}
                      </Tag>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                No products found
              </Text>
            )}
          </CollapsibleSection>

          {/* DOCUMENTS (collapsible) */}
          <Divider variant="dashed" />
          <CollapsibleSection title="DOCUMENTS">
            {(!packageOrder?.documents ||
              packageOrder?.documents?.length === 0) && (
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                No documents found
              </Text>
            )}
            {packageOrder?.documents && packageOrder.documents.length > 0 && (
              <div className="space-y-4">
                {sortedCategories.map((category) => (
                  <div key={category}>
                    <Text
                      size="xs"
                      fw={300}
                      className="mb-2 text-doubleNickel-gray-700"
                    >
                      {DocumentCategoryLabel[category]}
                    </Text>
                    <AttachmentsRow
                      attachments={documentsByCategory[category]}
                      applicantId={applicantId}
                      wrap={true}
                      showActions={true}
                      refetchQueries={approveRefetchQueries}
                    />
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>

          {/* ACTIVITY LOGS (collapsible) */}
          <Divider variant="dashed" />
          <CollapsibleSection title="ACTIVITY LOGS">
            <DnStepper
              items={sortedActivityLogs.map((activityLog) => ({
                id: activityLog.activityLogId,
                title: activityLog.title,
                content: activityLog.content,
                createdAt: activityLog.createdAt,
              }))}
              emptyMessage="No activity logs found"
            />
          </CollapsibleSection>
        </div>
      </div>
    </DnDrawer>
  )
}

export default OrderDetails
